import React, { useState, useMemo, useCallback } from "react";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { IconButton, Box, Tooltip, useTheme, TextField, Button, InputAdornment } from "@mui/material";

import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';

const SecurityMasterTable = ({ securities = [], onEdit, onDelete, onAdd, isLoading }) => {
  const theme = useTheme();
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(1000);

  // Memoized data processing - only runs when securities change
  const securityData = useMemo(() => {
    if (Array.isArray(securities)) {
      return securities;
    }
    if (securities?.securityMasterItems && Array.isArray(securities.securityMasterItems)) {
      return securities.securityMasterItems;
    }
    if (securities?.securities && Array.isArray(securities.securities)) {
      return securities.securities;
    }
    return [];
  }, [securities]);

  // Memoized filtered rows - only recalculates when search or securityData changes
  const filteredRows = useMemo(() => {
    if (!search) return securityData;
    const s = search.toLowerCase();
    return securityData.filter(row => {
      const tickerSymbol = row.ticker?.symbol || row.tickerSymbol || "";
      const name = row.name || "";
      const cusip = row.cusip || "";
      const sedol = row.sedol || "";
      const isin = row.isin || "";
      const figi = row.figi || "";
      const bloomberg = row.bloomberg || "";
      const factset = row.factset || "";
      
      return tickerSymbol.toLowerCase().includes(s) ||
             name.toLowerCase().includes(s) ||
             cusip.toLowerCase().includes(s) ||
             sedol.toLowerCase().includes(s) ||
             isin.toLowerCase().includes(s) ||
             figi.toLowerCase().includes(s) ||
             bloomberg.toLowerCase().includes(s) ||
             factset.toLowerCase().includes(s);
    });
  }, [securityData, search]);

  // Memoized rows with IDs - only recalculates when filteredRows changes
  const rows = useMemo(() => {
    return filteredRows.map((row, index) => ({ 
      ...row, 
      onEdit, 
      onDelete, 
      id: row.id || row.tickerId || index 
    }));
  }, [filteredRows, onEdit, onDelete]);

  // Memoized custom sort comparator - only created once
  const tickerSortComparator = useCallback((v1, v2) => {
    // Natural sorting: letters first, then numbers
    const isLetter1 = /^[A-Za-z]/.test(v1);
    const isLetter2 = /^[A-Za-z]/.test(v2);
    
    if (isLetter1 && !isLetter2) return -1; // v1 is letter, v2 is number
    if (!isLetter1 && isLetter2) return 1;  // v1 is number, v2 is letter
    
    // Both are same type, use regular string comparison
    return v1.localeCompare(v2, undefined, { numeric: true, sensitivity: 'base' });
  }, []);

  // Memoized columns - only created once
  const columns = useMemo(() => [
    { 
      field: "tickerSymbol", 
      headerName: "Ticker", 
      flex: 1, 
      minWidth: 120,
      sortComparator: tickerSortComparator,
      valueGetter: (params) => {
        return params.row.ticker?.symbol || params.row.tickerSymbol || "";
      },
      renderCell: (params) => {
        const tickerSymbol = params.row.ticker?.symbol || params.row.tickerSymbol || "";
        return (
          <Box sx={{ fontWeight: 600 }}>
            {tickerSymbol.toUpperCase()}
          </Box>
        );
      }
    },
    { 
      field: "name", 
      headerName: "Security Name", 
      flex: 1.5, 
      minWidth: 200 
    },
    { 
      field: "cusip", 
      headerName: "CUSIP", 
      flex: 1, 
      minWidth: 120 
    },
    { 
      field: "sedol", 
      headerName: "SEDOL", 
      flex: 1, 
      minWidth: 120 
    },
    { 
      field: "isin", 
      headerName: "ISIN", 
      flex: 1, 
      minWidth: 120 
    },
    { 
      field: "figi", 
      headerName: "FIGI", 
      flex: 1, 
      minWidth: 120 
    },
    { 
      field: "bloomberg", 
      headerName: "Bloomberg", 
      flex: 1, 
      minWidth: 120,
      valueGetter: (params) => {
        const row = params.row;
        
        // Only show values if they exist and are not null/undefined
        const ticker = row.bloomberg_ticker;
        const exchange = row.bloomberg_exch_code;
        const assetType = row.bloomberg_asset_type;
        
        const parts = [];
        if (ticker) parts.push(ticker);
        if (exchange) parts.push(exchange);
        if (assetType) parts.push(assetType);
        
        return parts.join(' ');
      }
    },
    { 
      field: "factset", 
      headerName: "FactSet", 
      flex: 1, 
      minWidth: 120,
      valueGetter: (params) => {
        const row = params.row;
        
        // Only show values if they exist and are not null/undefined
        const ticker = row.factset_ticker;
        const countryCode = row.factset_country_code;
        
        const parts = [];
        if (ticker) parts.push(ticker);
        if (countryCode) parts.push(countryCode);
        
        return parts.join(' ');
      }
    },
  ], [tickerSortComparator]);

  if (isLoading) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <div>Loading securities...</div>
      </Box>
    );
  }

  if (securityData.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <div>No securities data found.</div>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
        }}
      >
        <TextField
          size="small"
          placeholder="Search securities..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "text.disabled" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: 300,
            "& .MuiOutlinedInput-root": {
              borderRadius: 1,
            },
          }}
        />

        {/* <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onAdd}
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
            },
          }}
        >
          Add Security
        </Button> */}
      </Box>
      
      <DataGridPro
        rows={rows}
        columns={columns}
        pageSize={pageSize}
        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
        rowsPerPageOptions={[25, 50, 100, 500, 1000, 2000]}
        pagination
        density="compact"
        disableSelectionOnClick
        initialState={{
          sorting: {
            sortModel: [
              {
                field: 'tickerSymbol',
                sort: 'asc',
              },
            ],
          },
        }}
        sx={{
          border: "none",
          height: "calc(100vh - 20px)",
          "& .MuiDataGrid-cell": {
            borderBottom: `1px solid ${theme.palette.divider}`,
            color: theme.palette.text.primary,
            fontWeight: 500,
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.grey[100],
            borderBottom: `2px solid ${theme.palette.divider}`,
          },
          "& .MuiDataGrid-columnHeader": {
            fontWeight: 600,
            color: theme.palette.text.primary,
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: theme.palette.action.hover,
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: "transparent",
          },
        }}
      />
    </Box>
  );
};

export default SecurityMasterTable; 